import React, { useEffect, useState } from 'react';
import { Sidebar, Sidenav, Nav, Navbar, Stack, Badge, Button } from 'rsuite';
import { NavLinkProps, NavLink as BaseNavLink } from 'react-router-dom';
import ArrowLeftLineIcon from '@rsuite/icons/ArrowLeftLine';
import ArrowRightLineIcon from '@rsuite/icons/ArrowRightLine';
import { useAppDispatch, useAppSelector } from '@monorepo/shared';
import {
  isExpanded,
  selectSidemenu,
  updateExpanded,
  NavItemData
} from '@monorepo/shared/store/sidemenuSlice';
import { FortinetImage } from '@monorepo/shared/components';

interface IProps {
  navs: NavItemData[];
  windowHeight: number;
}
interface NavToggleProps {
  expand?: boolean;
  onChange?: () => void;
}

const NavIcon = (props: Record<string, string>) => {
  const { name, family, size, color, left, top, badgeText } = props;

  return badgeText ? (
    <Badge
      color={'orange'}
      content={badgeText}
      className="rs-sidenav-item-icon rs-icon"
      style={{ left: left, top: top }}
    >
      <i
        className={`icon-${family || 'fortinetone'}-${name} `}
        style={{
          fontSize: size,
          color: color
        }}
      />
    </Badge>
  ) : (
    <i
      className={`icon-${
        family || 'fortinetone'
      }-${name} rs-sidenav-item-icon rs-icon`}
      style={{ fontSize: size, color: color, left: left, top: top }}
    />
  );
};
const NavToggle = ({ expand, onChange }: NavToggleProps) => {
  return (
    <Navbar appearance="subtle" className="nav-toggle">
      <Nav pullRight>
        <Nav.Item
          onClick={onChange}
          style={{ textAlign: 'center' }}
          icon={expand ? <ArrowLeftLineIcon /> : <ArrowRightLineIcon />}
        />
      </Nav>
    </Navbar>
  );
};

const NavItem = (props: Record<string, any>) => {
  const { title, eventKey, to, myIcon, isLink, ...rest } = props;

  return (
    <Nav.Item
      eventKey={eventKey}
      as={isLink ? NavLink : NavButton}
      to={to}
      data-icon={myIcon}
      {...rest}
    >
      {myIcon?.name ? (
        <NavIcon
          name={myIcon.name}
          family={myIcon.family}
          size={myIcon.size}
          color={myIcon.color}
          left={myIcon.left}
          top={myIcon.top}
          badgeText={myIcon.badgeText}
        ></NavIcon>
      ) : (
        <></>
      )}
      {title}
    </Nav.Item>
  );
};

const NavButton = React.forwardRef<HTMLButtonElement, any>(
  ({ onClick, children, ...rest }, ref) => {
    return (
      <Button
        ref={ref}
        onClick={onClick}
        {...rest}
        appearance="link"
        style={{ height: rest?.['data-icon']?.height }}
      >
        {children}
      </Button>
    );
  }
);

const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ to, children, ...rest }, ref) => {
    return (
      <BaseNavLink ref={ref} to={to} {...rest}>
        {children}
      </BaseNavLink>
    );
  }
);

const Sidemenu = () => {
  const dispatch = useAppDispatch();
  const menuExpanded = useAppSelector(isExpanded);
  const navs = useAppSelector(selectSidemenu);
  const navBodyStyle: React.CSSProperties = {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden'
  };
  const [activeKey, setActiveKey] = useState<string>('');
  useEffect(() => {
    if (navs?.length) {
      const defaultActive = navs.find((obj: NavItemData) => obj?.isDefault);
      if (defaultActive) {
        setActiveKey(defaultActive.eventKey);
      }
    }
  }, [navs]);
  return (
    <Sidebar
      width={menuExpanded ? 260 : 72}
      collapsible
      className="frame-sidebar"
    >
      <Sidenav
        expanded={menuExpanded}
        appearance="subtle"
        defaultOpenKeys={['2', '3']}
      >
        <Sidenav.Body style={navBodyStyle}>
          <Nav activeKey={activeKey} onSelect={setActiveKey}>
            {navs?.length ? (
              navs.map((item) => {
                const { children, ...rest } = item;
                if (children) {
                  return (
                    <Nav.Menu
                      key={item.eventKey}
                      placement="rightStart"
                      trigger="hover"
                      {...rest}
                    >
                      {children.map((child: NavItemData) => {
                        return <NavItem key={child.eventKey} {...child} />;
                      })}
                    </Nav.Menu>
                  );
                }

                if (rest.target === '_blank') {
                  return (
                    <Nav.Item key={item.eventKey} {...rest}>
                      {item.title}
                    </Nav.Item>
                  );
                }

                return <NavItem key={rest.eventKey} {...rest} />;
              })
            ) : (
              <></>
            )}
          </Nav>
        </Sidenav.Body>
      </Sidenav>
    </Sidebar>
  );
};

export default Sidemenu;
